// Predictive Analytics Dashboard - Focus on actionable predictions
// Combines fill probability, market impact, and direction predictions

import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, Clock, Target, AlertTriangle, CheckCircle, Zap } from 'lucide-react';
import type { OrderBookEntry } from '../../types';
import type { EnhancedObservableAnalytics } from '../../utils/observableIntegration';
import { ObservableAnalyticsIntegrator } from '../../utils/observableIntegration';

interface PredictiveAnalyticsProps {
  baseAnalytics: any;
  orderBook: { bids: OrderBookEntry[]; asks: OrderBookEntry[] };
  recentTrades?: any[];
  currentPrice?: number;
}

interface PredictionScenario {
  side: 'buy' | 'sell';
  size: number;
  price: number;
  strategy: 'aggressive' | 'passive' | 'mid';
}

export function PredictiveAnalytics({
  baseAnalytics,
  orderBook,
  recentTrades = [],
  currentPrice
}: PredictiveAnalyticsProps) {
  const [integrator] = useState(() => new ObservableAnalyticsIntegrator());
  const [enhancedData, setEnhancedData] = useState<EnhancedObservableAnalytics | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<PredictionScenario>({
    side: 'buy',
    size: 1000,
    price: currentPrice || 0,
    strategy: 'mid'
  });
  const [scenarios, setScenarios] = useState<PredictionScenario[]>([]);

  useEffect(() => {
    integrator.updateMarketData(orderBook, recentTrades);

    if (currentPrice && currentPrice > 0) {
      // Generate multiple scenarios for comparison
      const bestBid = orderBook.bids[0]?.price || currentPrice;
      const bestAsk = orderBook.asks[0]?.price || currentPrice;
      const mid = (bestBid + bestAsk) / 2;
      const spread = bestAsk - bestBid;

      const newScenarios: PredictionScenario[] = [
        // Buy scenarios
        { side: 'buy', size: 1000, price: bestAsk, strategy: 'aggressive' },
        { side: 'buy', size: 1000, price: mid, strategy: 'mid' },
        { side: 'buy', size: 1000, price: bestBid, strategy: 'passive' },
        // Sell scenarios
        { side: 'sell', size: 1000, price: bestBid, strategy: 'aggressive' },
        { side: 'sell', size: 1000, price: mid, strategy: 'mid' },
        { side: 'sell', size: 1000, price: bestAsk, strategy: 'passive' },
      ];

      setScenarios(newScenarios);

      // Update selected scenario price if it's outdated
      if (Math.abs(selectedScenario.price - mid) > spread * 2) {
        setSelectedScenario(prev => ({ ...prev, price: mid }));
      }
    }
  }, [integrator, orderBook, recentTrades, currentPrice]);

  useEffect(() => {
    if (selectedScenario.price > 0) {
      const enhanced = integrator.generateEnhancedAnalytics(
        baseAnalytics,
        orderBook,
        selectedScenario
      );
      setEnhancedData(enhanced);
    }
  }, [integrator, baseAnalytics, orderBook, selectedScenario]);

  if (!enhancedData || !currentPrice) {
    return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
      Loading predictive analytics...
    </div>;
  }

  const getStrategyColor = (strategy: string) => {
    switch (strategy) {
      case 'aggressive': return '#ef4444';
      case 'mid': return '#f59e0b';
      case 'passive': return '#10b981';
      default: return '#94a3b8';
    }
  };

  const getConfidenceIcon = (confidence: string) => {
    switch (confidence) {
      case 'high': return <CheckCircle size={14} style={{ color: '#10b981' }} />;
      case 'medium': return <Clock size={14} style={{ color: '#f59e0b' }} />;
      default: return <AlertTriangle size={14} style={{ color: '#ef4444' }} />;
    }
  };

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.round(seconds / 60)}m`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Scenario Selection */}
      <div style={{
        backgroundColor: '#1e293b',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Target size={16} style={{ color: '#3b82f6' }} />
          <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
            Order Scenario Analysis
          </h4>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
          <div>
            <label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              Side
            </label>
            <select
              value={selectedScenario.side}
              onChange={(e) => setSelectedScenario(prev => ({
                ...prev,
                side: e.target.value as 'buy' | 'sell'
              }))}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: '#334155',
                color: '#e2e8f0',
                border: '1px solid #475569',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              Size
            </label>
            <input
              type="number"
              value={selectedScenario.size}
              onChange={(e) => setSelectedScenario(prev => ({
                ...prev,
                size: parseInt(e.target.value) || 1000
              }))}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: '#334155',
                color: '#e2e8f0',
                border: '1px solid #475569',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              Price
            </label>
            <input
              type="number"
              step="0.01"
              value={selectedScenario.price}
              onChange={(e) => setSelectedScenario(prev => ({
                ...prev,
                price: parseFloat(e.target.value) || currentPrice
              }))}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: '#334155',
                color: '#e2e8f0',
                border: '1px solid #475569',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            />
          </div>

          <div>
            <label style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
              Strategy
            </label>
            <select
              value={selectedScenario.strategy}
              onChange={(e) => setSelectedScenario(prev => ({
                ...prev,
                strategy: e.target.value as 'aggressive' | 'passive' | 'mid'
              }))}
              style={{
                width: '100%',
                padding: '6px',
                backgroundColor: '#334155',
                color: '#e2e8f0',
                border: '1px solid #475569',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              <option value="aggressive">Aggressive</option>
              <option value="mid">Mid</option>
              <option value="passive">Passive</option>
            </select>
          </div>
        </div>

        {/* Quick Scenario Buttons */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {scenarios.map((scenario, index) => (
            <button
              key={index}
              onClick={() => setSelectedScenario(scenario)}
              style={{
                padding: '6px 12px',
                backgroundColor: selectedScenario.side === scenario.side &&
                                selectedScenario.strategy === scenario.strategy ?
                                getStrategyColor(scenario.strategy) + '40' : '#374151',
                color: getStrategyColor(scenario.strategy),
                border: `1px solid ${getStrategyColor(scenario.strategy)}60`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: '600'
              }}
            >
              {scenario.side.toUpperCase()} {scenario.strategy.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Fill Probability Prediction */}
      {enhancedData.observable.fillProbability && (
        <div style={{
          backgroundColor: '#1e293b',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <TrendingUp size={16} style={{ color: '#10b981' }} />
            <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
              Fill Probability Prediction
            </h4>
            {getConfidenceIcon(enhancedData.observable.fillProbability.confidence)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                {(enhancedData.observable.fillProbability.probability * 100).toFixed(0)}%
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Fill Probability</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                {formatTime(enhancedData.observable.fillProbability.estimatedWaitTime)}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Est. Wait Time</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                {enhancedData.observable.fillProbability.factors.queuePosition}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Queue Position</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>
                {enhancedData.observable.fillProbability.dataPoints}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Data Points</div>
            </div>
          </div>

          <div style={{
            padding: '8px 12px',
            backgroundColor: '#0f172a',
            borderRadius: '4px',
            border: '1px solid #334155',
            fontSize: '10px',
            color: '#94a3b8'
          }}>
            <strong>Methodology:</strong> {enhancedData.observable.fillProbability.methodology.replace('_', ' ')}
            {enhancedData.observable.fillProbability.assumptions.length > 0 && (
              <div style={{ marginTop: '4px' }}>
                <strong>Assumptions:</strong> {enhancedData.observable.fillProbability.assumptions.join('; ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Market Impact Prediction */}
      {enhancedData.observable.marketImpact && (
        <div style={{
          backgroundColor: '#1e293b',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #334155'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Zap size={16} style={{ color: '#f59e0b' }} />
            <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
              Market Impact Prediction
            </h4>
            {getConfidenceIcon(enhancedData.observable.marketImpact.confidence)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#ef4444' }}>
                {enhancedData.observable.marketImpact.directImpact.toFixed(3)}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Direct Impact</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>
                {enhancedData.observable.marketImpact.estimatedTemporaryImpact.toFixed(3)}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Temporary Impact</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#8b5cf6' }}>
                {enhancedData.observable.marketImpact.levelsAffected}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Levels Affected</div>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#0f172a',
              borderRadius: '6px',
              border: '1px solid #334155',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                {enhancedData.observable.marketImpact.liquidityConsumed.toLocaleString()}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>Liquidity Used</div>
            </div>
          </div>

          {enhancedData.observable.marketImpact.remainingSize > 0 && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fbbf2420',
              borderRadius: '4px',
              border: '1px solid #fbbf2440',
              fontSize: '11px',
              color: '#fbbf24',
              marginBottom: '8px'
            }}>
              ⚠️ Partial fill expected: {enhancedData.observable.marketImpact.remainingSize} shares would remain unfilled
            </div>
          )}

          <div style={{
            padding: '8px 12px',
            backgroundColor: '#0f172a',
            borderRadius: '4px',
            border: '1px solid #334155',
            fontSize: '10px',
            color: '#94a3b8'
          }}>
            <strong>Method:</strong> Book simulation with {enhancedData.observable.marketImpact.factors.bookDepth.toLocaleString()} depth
          </div>
        </div>
      )}

      {/* Market Direction & Timing */}
      <div style={{
        backgroundColor: '#1e293b',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #334155'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Brain size={16} style={{ color: '#3b82f6' }} />
          <h4 style={{ margin: '0', fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>
            Market Direction & Timing
          </h4>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
          <div style={{
            padding: '12px',
            backgroundColor: enhancedData.predictions.nextMoveDirection === 'up' ? '#10b98120' :
                            enhancedData.predictions.nextMoveDirection === 'down' ? '#ef444420' : '#94a3b820',
            borderRadius: '6px',
            border: `1px solid ${
              enhancedData.predictions.nextMoveDirection === 'up' ? '#10b98140' :
              enhancedData.predictions.nextMoveDirection === 'down' ? '#ef444440' : '#94a3b840'
            }`
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: enhancedData.predictions.nextMoveDirection === 'up' ? '#10b981' :
                    enhancedData.predictions.nextMoveDirection === 'down' ? '#ef4444' : '#94a3b8',
              textAlign: 'center'
            }}>
              {enhancedData.predictions.nextMoveDirection.toUpperCase()}
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>
              Predicted Direction
            </div>
          </div>

          <div style={{
            padding: '12px',
            backgroundColor: '#0f172a',
            borderRadius: '6px',
            border: '1px solid #334155',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
              {(enhancedData.predictions.confidence * 100).toFixed(0)}%
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8' }}>Confidence</div>
          </div>

          <div style={{
            padding: '12px',
            backgroundColor: '#0f172a',
            borderRadius: '6px',
            border: '1px solid #334155',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#8b5cf6' }}>
              {enhancedData.predictions.timeHorizon}
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8' }}>Time Horizon</div>
          </div>
        </div>

        {enhancedData.predictions.reasoning.length > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            backgroundColor: '#0f172a',
            borderRadius: '4px',
            border: '1px solid #334155'
          }}>
            <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '6px' }}>
              <strong>Analysis Factors:</strong>
            </div>
            {enhancedData.predictions.reasoning.map((reason, index) => (
              <div key={index} style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '2px' }}>
                • {reason}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}